import { Link } from "react-router-dom";
import { ArrowRight, Check, Mic, FileText, Send, Quote } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Seo } from "./Seo";
import { softwareApplication, faq as faqSchema, breadcrumb, organization } from "./schema";

const UI = {
  fr: { cta: "Essayer gratuitement", ctaSub: "30 jours gratuits · sans carte bancaire", how: "Comment ça marche", faq: "Questions fréquentes", more: "Pour aller plus loin", home: "Accueil", testimonial: "Témoignage", bottom: "Prêt à gagner vos soirées ?", bottomText: "Créez votre compte en 2 minutes. Votre premier devis dicté est prêt aujourd'hui.", tarifs: "Voir les tarifs" },
  nl: { cta: "Gratis proberen", ctaSub: "30 dagen gratis · geen bankkaart nodig", how: "Hoe werkt het", faq: "Veelgestelde vragen", more: "Lees ook", home: "Home", testimonial: "Getuigenis", bottom: "Klaar om uw avonden terug te winnen?", bottomText: "Maak uw account aan in 2 minuten. Uw eerste gedicteerde offerte is vandaag klaar.", tarifs: "Bekijk de prijzen" },
};
const STEP_ICONS = [Mic, FileText, Send];

const Img = ({ img, className = "", eager = false }) => (
  <img src={img.src} alt={img.alt} width={img.width} height={img.height} loading={eager ? "eager" : "lazy"} decoding="async" fetchPriority={eager ? "high" : undefined} className={`w-full h-auto ${className}`} />
);

const Cta = ({ to, label, testId, light }) => (
  <Link to={to} data-testid={testId} className={`inline-flex items-center gap-2 rounded-xl px-6 py-3.5 text-base font-semibold shadow-lg transition-transform duration-150 hover:-translate-y-0.5 ${light ? "bg-white text-[#0F2645]" : "bg-[#3A5CC8] text-white hover:bg-[#2F4CA8]"}`}>
    {label} <ArrowRight className="h-4 w-4" />
  </Link>
);

export const LandingPage = ({ content, path }) => {
  const ui = UI[content.lang] || UI.fr;
  const signup = "/studio/inscription";
  const jsonLd = [organization(), softwareApplication(content.lang), faqSchema(content.faq), breadcrumb([{ name: ui.home, path: content.lang === "nl" ? "/nl" : "/" }, { name: content.breadcrumb, path }])];
  return (
    <div data-testid={`landing-${content.key}`} className="font-body min-h-screen bg-[#F6F8FD] text-[#0F2645]">
      <Seo path={path} jsonLd={jsonLd} preloadImage={content.hero?.src} />
      <Header />
      <main className="pt-20">
        <section className="mx-auto grid max-w-7xl items-center gap-10 px-6 py-14 sm:py-20 lg:grid-cols-2 lg:px-8">
          <div>
            <p className="text-sm font-bold uppercase tracking-[0.12em] text-[#3A5CC8]">{content.eyebrow}</p>
            <h1 data-testid="landing-h1" className="mt-4 font-heading text-4xl font-bold leading-[1.08] tracking-tight sm:text-5xl lg:text-6xl">{content.h1}</h1>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-[#5C6A87]">{content.lead}</p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Cta to={signup} label={ui.cta} testId="landing-cta-top" />
              <Link to="/tarifs" className="text-sm font-semibold text-[#3A5CC8] underline-offset-4 hover:underline">{ui.tarifs}</Link>
            </div>
            <p className="mt-3 text-xs text-[#93A0BC]">{ui.ctaSub}</p>
          </div>
          <div className="overflow-hidden rounded-2xl border border-[#D9E0F2] bg-white shadow-xl"><Img img={content.hero} eager /></div>
        </section>

        {content.sections.map((s, i) => (
          <section key={i} className={`${i % 2 ? "bg-white" : ""} py-14 sm:py-16`}>
            <div className={`mx-auto grid max-w-7xl gap-10 px-6 lg:px-8 ${s.image ? "items-center lg:grid-cols-2" : ""}`}>
              <div className={s.image && i % 2 ? "lg:order-2" : ""}>
                <h2 className="font-heading text-2xl font-semibold tracking-tight sm:text-3xl">{s.h2}</h2>
                {s.paragraphs.map((p, j) => <p key={j} className="mt-4 text-base leading-relaxed text-[#5C6A87]">{p}</p>)}
                {s.bullets && (
                  <ul className="mt-5 space-y-2.5">
                    {s.bullets.map((b, j) => <li key={j} className="flex items-start gap-3 text-base text-[#0F2645]"><Check className="mt-1 h-4 w-4 shrink-0 text-[#16A34A]" />{b}</li>)}
                  </ul>
                )}
              </div>
              {s.image && <div className="overflow-hidden rounded-2xl border border-[#D9E0F2] bg-white shadow-lg"><Img img={s.image} /></div>}
            </div>
          </section>
        ))}

        <section className="bg-[#0F2645] py-16 text-white">
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <h2 className="font-heading text-2xl font-semibold tracking-tight sm:text-3xl">{ui.how}</h2>
            <div className="mt-10 grid gap-8 md:grid-cols-3">
              {content.steps.map((st, i) => {
                const Icon = STEP_ICONS[i] || Check;
                return (
                  <div key={i} className="rounded-2xl border border-white/10 bg-white/[0.05] p-6">
                    <div className="flex items-center gap-3"><span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#3A5CC8]"><Icon className="h-5 w-5" /></span><span className="text-xs font-bold uppercase tracking-[0.14em] text-[#9FB4F2]">0{i + 1}</span></div>
                    <h3 className="mt-4 font-heading text-lg font-semibold">{st.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-[#C8D2EC]">{st.text}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <section className="py-16">
          <div className="mx-auto max-w-4xl px-6 lg:px-8">
            <p className="text-sm font-bold uppercase tracking-[0.12em] text-[#3A5CC8]">{ui.testimonial}</p>
            <blockquote className="mt-4 rounded-2xl border border-[#D9E0F2] bg-white p-8 shadow-sm">
              <Quote className="h-6 w-6 text-[#3A5CC8]" />
              <p className="mt-4 text-lg leading-relaxed text-[#0F2645]">« {content.testimonial.quote} »</p>
              <footer className="mt-5 text-sm text-[#5C6A87]"><strong className="font-semibold text-[#0F2645]">{content.testimonial.author}</strong> — {content.testimonial.role}</footer>
            </blockquote>
          </div>
        </section>

        <section className="bg-white py-16">
          <div className="mx-auto max-w-4xl px-6 lg:px-8">
            <h2 className="font-heading text-2xl font-semibold tracking-tight sm:text-3xl">{ui.faq}</h2>
            <div className="mt-8 divide-y divide-[#E2E7F7] rounded-2xl border border-[#D9E0F2]">
              {content.faq.map((f, i) => (
                <details key={i} data-testid={`landing-faq-${i}`} className="group px-6 py-4">
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-base font-semibold text-[#0F2645]">{f.q}<span className="text-[#3A5CC8] transition-transform duration-150 group-open:rotate-45">+</span></summary>
                  <p className="mt-3 text-base leading-relaxed text-[#5C6A87]">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16">
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <h2 className="font-heading text-xl font-semibold tracking-tight">{ui.more}</h2>
            <div className="mt-6 grid gap-5 md:grid-cols-3">
              {content.links.map((l) => (
                <Link key={l.to} to={l.to} className="rounded-2xl border border-[#D9E0F2] bg-white p-5 transition-shadow hover:shadow-md">
                  <span className="block font-semibold text-[#0F2645]">{l.label}</span>
                  <span className="mt-1 block text-sm text-[#5C6A87]">{l.text}</span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-[#3A5CC8] py-16 text-white">
          <div className="mx-auto max-w-4xl px-6 text-center lg:px-8">
            <h2 className="font-heading text-2xl font-semibold tracking-tight sm:text-3xl">{ui.bottom}</h2>
            <p className="mt-3 text-base text-white/85">{ui.bottomText}</p>
            <div className="mt-8"><Cta to={signup} label={ui.cta} testId="landing-cta-bottom" light /></div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};
